import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Flex, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useAddProductItems } from '../../bff-api/baskets/useAddProductItems';
import { useBasket } from '../../bff-api/baskets/useBasket';
import { useDeleteProductItem } from '../../bff-api/baskets/useDeleteProductItem';
import { SHIPPING_PROTECTION_PRODUCT_ID } from '../../constants';
import { useGTM } from '../../hooks';
import { GTMAddToBagEvent } from '../../hooks/gtm/model/events/GTMAddToBagEvent';
import { useShippingProtectionConfigs } from '../../hooks/useShippingProtectionConfigs';
import { PageSource } from '../../types/page-source';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import {
    getShippingProtectionItems,
    isShippableProductForProtection,
} from '../../utils/product-utils';
import { InfoIcon, SafoniaNameIcon } from '../icons';
import { ShippingProtectionModal } from './ShippingProtectionModal';
import type { Basket, ProductItem } from '../../bff-api/baskets/model';
import type { Product } from '../../bff-api/products/model';
import type { GTMSourceEvent } from '../../hooks/gtm/model/events/GTMSourceEvent';

/**
 * Calculate the price for Safonia shipping protection with fallback chain.
 * Priority: basket item price > c_shippingProtectionFee (if valid number > 0) > fallback price
 *
 * @internal Exported for testing purposes
 */
export const getSafoniaPrice = (
    basketItem: ProductItem | undefined,
    shippingProtectionFee: unknown,
    fallbackPrice: number,
): number => {
    // First priority: Use basket item price if available
    if (basketItem?.price != null) return basketItem.price;

    // Second priority: Use c_shippingProtectionFee if it's a valid positive number
    const fee = Number(shippingProtectionFee);
    if (!Number.isNaN(fee) && fee > 0) return fee;

    // Final fallback: Use the product's original price
    return fallbackPrice;
};

interface ShippingProtectionToggleProps {
    shippingProtectionProduct?: Product;
    isLoading?: boolean;
    pageSource?: PageSource;
}

export const ShippingProtectionToggle: FC<ShippingProtectionToggleProps> = ({
    shippingProtectionProduct,
    isLoading: isProductLoading = false,
    pageSource,
}) => {
    const { isDefaultBehaviorActive } = useShippingProtectionConfigs();
    const { onAddToBag } = useGTM();

    const underlineComponent = useCallback((chunks: React.ReactNode[]) => <u>{chunks}</u>, []);

    const shippingProtectionSiteUrl = useCustomPreferenceOrDefault<string>(
        CustomPreference.SHIPPING_PROTECTION_SITE_URL,
        'https://www.surlatable.com/page-not-found',
    );

    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data: basket } = useBasket();
    const optedOutOfShippingProtection = basket?.c_optedOutOfShippingProtection; // default to false

    // Determine GTM source based on page context
    const gtmSource: GTMSourceEvent = pageSource === PageSource.CHECKOUT ? 'CHECKOUT' : 'BAG';

    // Helper function to fire GTM tracking when safonia is added
    const fireAddToBagGTM = useCallback(
        (updatedBasket: Basket, product: Product) => {
            // Get the actual dynamic price from basket item, fallback to basket's shipping protection fee
            const safoniaItemInBasket = updatedBasket.productItems?.find(
                (item) => item.productId === product.id,
            );

            // Calculate price with safe fallback chain to avoid NaN
            const calculatedPrice = getSafoniaPrice(
                safoniaItemInBasket,
                updatedBasket.c_shippingProtectionFee,
                product.price ?? 0,
            );

            const productWithActualPrice: Product = {
                ...product,
                price: calculatedPrice,
                // Preserve existing master properties while overriding masterId
                // Safe to overwrite since Safonia has no parent product by design
                master: {
                    ...product.master,
                    masterId: SHIPPING_PROTECTION_PRODUCT_ID,
                },
                c_quantityAddedToBag: 1,
            };

            onAddToBag(new GTMAddToBagEvent(gtmSource, updatedBasket, productWithActualPrice));
        },
        // Note: 'product' is not in deps because it's passed as a parameter to the callback
        [gtmSource, onAddToBag],
    );

    const addProductItemsMutation = useAddProductItems(pageSource);
    const deleteProductItemMutation = useDeleteProductItem('BAG', pageSource);

    // Local loading state to work around React Query notification batching issues
    const [isLocallyAdding, setIsLocallyAdding] = useState(false);
    const [isLocallyDeleting, setIsLocallyDeleting] = useState(false);

    const shippingProtectionItems = useMemo(
        () => (basket?.productItems ? getShippingProtectionItems(basket.productItems) : []),
        [basket?.productItems],
    );

    const hasShippingProtection = shippingProtectionItems.length > 0;

    const eligibleProducts = basket?.productItems?.filter(isShippableProductForProtection) || [];
    const hasEligibleProducts = eligibleProducts.length > 0;
    const autoAddRunKeyRef = useRef<string | null>(null);
    useEffect(() => {
        const key = `${basket?.basketId ?? 'no-basket'}:${
            shippingProtectionProduct?.id ?? 'no-prod'
        }`;

        if (
            optedOutOfShippingProtection ||
            !isDefaultBehaviorActive ||
            !shippingProtectionProduct ||
            !hasEligibleProducts ||
            hasShippingProtection ||
            autoAddRunKeyRef.current === key
        ) {
            return;
        }

        autoAddRunKeyRef.current = key;
        setIsLocallyAdding(true);
        addProductItemsMutation.mutate(
            [
                {
                    productId: shippingProtectionProduct.id,
                    quantity: 1,
                    c_addedToCartAt: new Date().toISOString(),
                    c_updatedInCartAt: new Date().toISOString(),
                },
            ],
            {
                onSuccess: (updatedBasket) => {
                    // Fire GTM tracking for auto-add
                    fireAddToBagGTM(updatedBasket, shippingProtectionProduct);
                },
            },
        );
    }, [
        isDefaultBehaviorActive,
        optedOutOfShippingProtection,
        hasEligibleProducts,
        hasShippingProtection,
        basket?.basketId,
        shippingProtectionProduct?.id,
        addProductItemsMutation,
        fireAddToBagGTM,
    ]);

    // Clear local loading state when Safonia appears/disappears in basket
    // This works around React Query notification batching issues when mutations overlap
    useEffect(() => {
        if (isLocallyAdding && hasShippingProtection) {
            setIsLocallyAdding(false);
        }
        if (isLocallyDeleting && !hasShippingProtection) {
            setIsLocallyDeleting(false);
        }
    }, [isLocallyAdding, isLocallyDeleting, hasShippingProtection]);

    const deleteRunKeyRef = useRef<string | null>(null);
    useEffect(() => {
        const currentItem = shippingProtectionItems[0];
        const key = `${basket?.basketId ?? 'no-basket'}:${currentItem?.itemId ?? 'no-item'}`;

        if (
            !hasEligibleProducts &&
            hasShippingProtection &&
            currentItem &&
            deleteRunKeyRef.current !== key
        ) {
            deleteRunKeyRef.current = key;
            setIsLocallyDeleting(true);
            deleteProductItemMutation.mutate({
                itemId: currentItem.itemId,
                productItem: currentItem,
            });
        }
    }, [hasEligibleProducts, hasShippingProtection, basket?.basketId, shippingProtectionItems]);

    const handleToggle = (checked: boolean): void => {
        if (!shippingProtectionProduct) return;

        if (checked) {
            setIsLocallyAdding(true);
            addProductItemsMutation.mutate(
                [
                    {
                        productId: shippingProtectionProduct.id,
                        quantity: 1,
                        c_addedToCartAt: new Date().toISOString(),
                        c_updatedInCartAt: new Date().toISOString(),
                    },
                ],
                {
                    onSuccess: (updatedBasket) => {
                        // Fire GTM tracking for manual toggle add
                        fireAddToBagGTM(updatedBasket, shippingProtectionProduct);
                    },
                },
            );
        } else if (shippingProtectionItems.length > 0) {
            const productItem = shippingProtectionItems[0];
            setIsLocallyDeleting(true);
            deleteProductItemMutation.mutate({
                itemId: productItem.itemId,
                productItem,
            });
        }
    };

    // Show loading state while fetching product
    if (isProductLoading) {
        return (
            <Flex direction="column" gap={2} p={4}>
                <Flex align="center" gap={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.600">
                        Loading shipping protection...
                    </Text>
                </Flex>
            </Flex>
        );
    }

    // Don't render if product not found
    if (!shippingProtectionProduct) {
        return null;
    }

    return (
        <Flex direction="column" gap={2} py={4} data-testid="shipping-protection-toggle-container">
            <Flex align="center" gap={2}>
                <Checkbox
                    isChecked={hasShippingProtection}
                    onChange={(e) => handleToggle(e.target.checked)}
                    isDisabled={isLocallyAdding || isLocallyDeleting || !hasEligibleProducts}
                >
                    <Flex align="center" gap={2}>
                        <SafoniaNameIcon data-testid="safonia-name-icon" />
                        <Text
                            textStyle={
                                pageSource === PageSource.CART
                                    ? 'protection-coverage-subtitle'
                                    : 'slt-body-text-lg'
                            }
                            color={
                                pageSource === PageSource.CART ? 'slt-pepper.1000' : 'slt-black.900'
                            }
                            fontWeight={500}
                        >
                            {basket?.c_shippingProtectionFee ? (
                                <FormattedMessage
                                    id="shipping.protection.price"
                                    // eslint-disable-next-line no-template-curly-in-string
                                    defaultMessage="Protection for ${price}"
                                    values={{ price: basket?.c_shippingProtectionFee }}
                                />
                            ) : (
                                <FormattedMessage defaultMessage="Add shipping protection!" />
                            )}
                        </Text>
                    </Flex>
                </Checkbox>
                <InfoIcon cursor="pointer" onClick={onOpen} />
                {isLocallyDeleting || isLocallyAdding ? (
                    <Spinner size="xs" color="slt-pepper.1000" />
                ) : null}
            </Flex>
            <Flex px={6}>
                {hasEligibleProducts && (
                    <Text textStyle="body-small" cursor="pointer" onClick={onOpen}>
                        <FormattedMessage
                            id="shipping.protection.perks.boost"
                            defaultMessage="Get <component>+25% Perks Boost</component>, with 100% Shipping and Delivery Protection"
                            values={{
                                component: underlineComponent,
                            }}
                        />
                    </Text>
                )}
                {!hasEligibleProducts && (
                    <Text textStyle="body-small" cursor="pointer" onClick={onOpen}>
                        Add eligible products to enable shipping protection.
                    </Text>
                )}
            </Flex>

            <ShippingProtectionModal
                isOpen={isOpen}
                onClose={onClose}
                siteUrl={shippingProtectionSiteUrl}
            />
        </Flex>
    );
};
